import { useApolloClient } from '@apollo/client';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { isEqual, sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItemInfo,
  Platform,
  RefreshControl,
  ScrollView,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  Carousel,
  CarouselPaging,
  CarouselRef,
  ErrorCard,
  GenericCard,
  IconOffer,
  IconRefresh,
  MqSwitch,
  PaginationDots,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { IconProfile } from '@fhs/ui/src/components/icon/svgs/profile';
import {
  LoyaltyUserDocument,
  LoyaltyUserTransactionsDocument,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { EventTypes } from '@fhs-legacy/frontend/src/state/crm-events';
import { CustomEventNames, useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { actions, useAppDispatch } from '@fhs-legacy/frontend/src/state/global-state';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import {
  OfferCard,
  OfferCardProps,
  PromoCode,
  RewardCard,
  RewardCardProps,
  RewardCardSkeleton,
  SeeMoreCard,
} from '../../components';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { LoyaltyIncentiveOffer, useLoyaltyIncentives } from '../../queries/loyalty.queries';
import { formatOfferCards } from '../../utils';

import { Challenges } from './challenges';
import { ChallengesSectionHeader } from './challenges-section-header';
import {
  Section,
  useIsLoyaltyPageLoading,
  useScrollManager,
  useSections,
  useShouldHideChallenges,
  useShouldShowSectionsHeader,
} from './hooks';
import { OffersDisclaimer } from './offers-disclaimer';
import { OffersSectionHeader } from './offers-section-header';
import { OffersSkeleton } from './offers-skeleton';
import { RewardTiers } from './reward-tiers';
import { RewardsSectionHeader } from './rewards-section-header';
import { SectionsHeader } from './sections-header';
import { UnauthenticatedWithNoOffers } from './unauthenticated-with-no-offers';

const scrollEventThrottle = Platform.select({
  ios: 300,
  android: 20,
  web: 300,
});

type RefreshStatus = 'loading' | 'ready' | 'error';

function renderOffer({ item }: ListRenderItemInfo<OfferCardProps>) {
  return <OfferCard {...item} />;
}

function renderReward({ item }: ListRenderItemInfo<RewardCardProps>) {
  return <RewardCard {...item} />;
}

export function OffersAndRewardsScreen() {
  const { bottom } = useSafeAreaInsets();
  const [measured, setMeasured] = useState(false);
  const { section } = useLocalSearchParams<{ section?: Section }>() || {};
  const isRewardsTiersEnabled = useFlag('enable-rewards-tiers');
  const { scrollViewRef, activeSection, scrollToSection, onScroll, measureSectionPosition } =
    useScrollManager();

  const sections = useSections(activeSection, scrollToSection);

  useEffect(() => {
    if (section && measured) {
      scrollToSection(section);
    }
  }, [section, scrollToSection, measured]);

  const { isAuthenticated } = useAuthContext();
  const mqStyles = useMqStyles();
  const { formatMessage } = useIntl();
  const { logRBIEvent } = useCRMEventsContext();
  const cardsToRender = useMqSelect({ $ltDesktop: 5 }, Infinity);
  const [carouselPaging, setCarouselPaging] = useState<CarouselPaging>();
  const carouselRef = useRef<CarouselRef>(null);
  const offersTrackedRef = useRef<{ id: string; name: string; cmsId: string }[]>();
  const challengesSectionRef = useRef<View>(null);
  const offersSectionRef = useRef<View>(null);
  const rewardsSectionRef = useRef<View>(null);

  const { data, isError, isFetching, refetch } = useLoyaltyIncentives();
  const apolloClient = useApolloClient();
  const dispatch = useAppDispatch();
  const { loyaltyId, loyaltyUserReady, store, serviceMode } = useLoyaltyLegacyStates();
  const [userRefreshStatus, setUserRefreshStatus] = useState<RefreshStatus>('loading');
  const [incentivesRefreshStatus, setIncentivesRefreshStatus] =
    useState<RefreshStatus>('loading');
  const [refreshAttempt, setRefreshAttempt] = useState(0);
  const latestRefreshAttempt = useRef(refreshAttempt);
  latestRefreshAttempt.current = refreshAttempt;

  const refreshRewards = useCallback(() => {
    setUserRefreshStatus('loading');
    setIncentivesRefreshStatus('loading');
    setRefreshAttempt(attempt => attempt + 1);
  }, []);

  // Keep refreshes separate: user history needs a loyalty ID; incentives need store/service mode
  // and support guests. Store changes should only trigger the incentives refresh.
  useFocusEffect(
    useCallback(() => {
      if (!isAuthenticated || !loyaltyId) {
        setUserRefreshStatus('ready');
        return;
      }

      let active = true;
      setUserRefreshStatus('loading');
      // The providers use these queries to update points and the daily redemption count.
      apolloClient
        .refetchQueries({ include: [LoyaltyUserDocument, LoyaltyUserTransactionsDocument] })
        .then(results => {
          if (!active || refreshAttempt !== latestRefreshAttempt.current) {
            return;
          }

          const transactions = results.find(result => result.data?.loyaltyUserV2?.transactions)
            ?.data?.loyaltyUserV2?.transactions;
          if (!transactions) {
            throw new Error('Loyalty transaction refresh did not return history');
          }
          // Update the CTA limit before dismissing the loading state.
          dispatch(actions.loyalty.setUserTransactions(transactions));
          setUserRefreshStatus('ready');
        })
        .catch(error => {
          logger.warn({ error, message: 'Unable to refresh loyalty user and transactions' });
          if (active && refreshAttempt === latestRefreshAttempt.current) {
            setUserRefreshStatus('error');
          }
        });

      return () => {
        active = false;
        // Block stale CTAs before the next focus effect starts.
        setUserRefreshStatus('loading');
      };
    }, [isAuthenticated, loyaltyId, apolloClient, dispatch, refreshAttempt])
  );

  useFocusEffect(
    useCallback(() => {
      if (!loyaltyUserReady || !store.number || !serviceMode) {
        return;
      }

      let active = true;
      setIncentivesRefreshStatus('loading');
      refetch()
        .then(result => {
          if (active && refreshAttempt === latestRefreshAttempt.current) {
            setIncentivesRefreshStatus(result.isError ? 'error' : 'ready');
          }
        })
        .catch(error => {
          logger.warn({ error, message: 'Unable to refresh Rewards incentives' });
          if (active && refreshAttempt === latestRefreshAttempt.current) {
            setIncentivesRefreshStatus('error');
          }
        });

      return () => {
        active = false;
        setIncentivesRefreshStatus('loading');
      };
    }, [loyaltyUserReady, store.number, serviceMode, refetch, refreshAttempt])
  );

  const shouldHideChallenges = useShouldHideChallenges();

  const shouldShowSectionsHeader = useShouldShowSectionsHeader();

  const isLoading = useIsLoyaltyPageLoading();

  const offerCardProps = useMemo(
    () => (isLoading ? [] : formatOfferCards(data?.offers, cardsToRender)),
    [data?.offers, cardsToRender, isLoading]
  );

  const rewardCardProps = useMemo(
    () =>
      isLoading
        ? []
        : data?.rewards?.reduce<RewardCardProps[]>((rewardCards, reward) => {
          if (rewardCards.length >= cardsToRender || !reward) {
            return rewardCards;
          }

          if (reward.image && typeof reward.points === 'number') {
            rewardCards.push({
              id: reward.id,
              points: reward.points,
              imageUrl: reward.image,
              title: reward.name,
              onPress: () => router.navigate(`${routes.loyaltyRewardsList}/${reward.id}`),
              isDisabled: !reward.isAvailable,
              buttonLabel: formatMessage({ id: 'redeemItem' }),
            });
          }

          return rewardCards;
        }, []) ?? [],
    [data?.rewards, formatMessage, cardsToRender, isLoading]
  );

  const hideCarouselButtons = !offerCardProps || offerCardProps.length === 0;

  useFocusEffect(
    useCallback(() => {
      const sortOrdersById = (offersToSort: { id: string; name: string }[] | undefined) =>
        sortBy(offersToSort, offer => offer.id);

      const extractOffersLogData = (offers: LoyaltyIncentiveOffer[]) =>
        offers.map(offer => ({
          id: offer.id ?? '',
          name: offer.name,
          cmsId: offer.cmsId,
        }));

      const extractRewardsLogData = (rewardTiers: NonNullable<typeof data>['rewardTiers']) =>
        rewardTiers?.map(tier => ({
          id: tier.id,
          name: tier.name,
          points: tier.points,
          ...(typeof tier.isEligible === 'boolean' && { isEligible: tier.isEligible }),
          ...(typeof tier.isRedeemable === 'boolean' && { isAvailable: tier.isRedeemable }),
        })) ?? [];

      const offers = extractOffersLogData(data?.offers);
      const rewards = extractRewardsLogData(data?.rewardTiers);

      if (
        !isLoading &&
        (!offersTrackedRef.current ||
          !isEqual(sortOrdersById(offersTrackedRef.current), sortOrdersById(offers)))
      ) {
        logRBIEvent({
          name: CustomEventNames.OFFERS_AND_REWARDS_LISTED,
          type: EventTypes.UserContent,
          attributes: {
            version: 'v2',
            offers,
            rewards,
          },
        });
        offersTrackedRef.current = offers;
      }
    }, [logRBIEvent, isLoading, data?.offers, data?.rewardTiers])
  );

  const nextCarouselPage = useCallback(() => {
    carouselRef.current?.nextPage();
  }, []);

  const isNextDisabled = useMemo(
    () => !carouselPaging || carouselPaging.currentPage === carouselPaging.totalPages - 1,
    [carouselPaging]
  );

  const prevCarouselPage = useCallback(() => {
    carouselRef.current?.prevPage();
  }, []);

  const isPrevDisabled = useMemo(
    () => !carouselPaging || carouselPaging?.currentPage === 0,
    [carouselPaging]
  );

  const goToPage = useCallback((page: number) => {
    carouselRef.current?.goToPage(page);
  }, []);

  const onPressSignUp = useCallback(() => {
    router.navigate(routes.signIn);
  }, []);

  const seeAllOffers = useCallback(() => {
    router.navigate('v2/offers/all');
  }, []);

  const seeAllRewards = useCallback(() => {
    router.navigate(routes.rewards);
  }, []);

  const seeRewardsHistory = useCallback(() => {
    router.navigate(routes.rewardsHistory);
  }, []);

  const measureSectionPositions = useCallback(() => {
    measureSectionPosition(challengesSectionRef, 'challenges');
    measureSectionPosition(offersSectionRef, 'offers');
    measureSectionPosition(rewardsSectionRef, 'rewards');
    setMeasured(true);
  }, [measureSectionPosition]);

  const renderOffersEmpty = useCallback(() => {
    if (isLoading) {
      return <OffersSkeleton style={mqStyles.emptyOffersContainer} />;
    }

    return (
      <GenericCard
        title={formatMessage({ id: 'noOffersAvailable' })}
        description={formatMessage({ id: 'noOffersAvailableMessage' })}
        hideButton
        icon={<IconOffer size={68} color={tokens.colors.$blackOpacity10} />}
        style={mqStyles.noOffersAvailable}
      />
    );
  }, [isLoading, mqStyles.noOffersAvailable, mqStyles.emptyOffersContainer, formatMessage]);

  const renderRewardsEmpty = useCallback(() => {
    if (isLoading) {
      return (
        <ScrollView
          horizontal
          style={mqStyles.emptyList}
          contentContainerStyle={mqStyles.emptyListContent}
          showsHorizontalScrollIndicator={false}
        >
          <RewardCardSkeleton />
          <RewardCardSkeleton />
          <RewardCardSkeleton />
          <RewardCardSkeleton />
          <RewardCardSkeleton />
        </ScrollView>
      );
    }
    return null;
  }, [isLoading, mqStyles.emptyList, mqStyles.emptyListContent]);

  const renderOffersFooter = useCallback(() => {
    if (isLoading || offerCardProps.length === 0) {
      return null;
    }
    return (
      <SeeMoreCard
        style={mqStyles.seeMoreOffers}
        title={formatMessage(
          { id: 'lookingForMore' },
          { incentive: formatMessage({ id: 'offers' }) }
        )}
        buttonLabel={formatMessage({ id: 'seeAll' })}
        onPress={seeAllOffers}
      />
    );
  }, [formatMessage, mqStyles.seeMoreOffers, isLoading, seeAllOffers, offerCardProps.length]);

  const renderRewardsFooter = useCallback(() => {
    if (isLoading || rewardCardProps.length === 0) {
      return null;
    }
    return (
      <SeeMoreCard
        style={mqStyles.seeMoreRewards}
        title={formatMessage(
          { id: 'lookingForMore' },
          { incentive: formatMessage({ id: 'rewards' }) }
        )}
        buttonLabel={formatMessage({ id: 'seeAll' })}
        onPress={seeAllRewards}
      />
    );
  }, [formatMessage, mqStyles.seeMoreRewards, isLoading, seeAllRewards, rewardCardProps.length]);

  const renderHeader = useCallback(() => {
    return <View style={mqStyles.listHeader} />;
  }, [mqStyles.listHeader]);

  const listHeader = useMqSelect({ $ltDesktop: renderHeader }, undefined);

  const renderCarouselLoading = useCallback(() => {
    return <OffersSkeleton />;
  }, []);

  const offerCardWidth = useMqSelect({ $gteDesktop: 215 }, 160);

  if (isError || userRefreshStatus === 'error' || incentivesRefreshStatus === 'error') {
    return (
      <ErrorCard
        description={formatMessage({ id: 'offersLoadingErrorMessage' })}
        buttonType="outline"
        buttonIcon={<IconRefresh color={tokens.colors.$houseRedDarken} />}
        buttonTitle="Reload"
        icon={<IconOffer size={68} color={tokens.colors.$blackOpacity10} />}
        buttonHandle={refreshRewards}
      />
    );
  }

  if (userRefreshStatus === 'loading' || incentivesRefreshStatus === 'loading' || isFetching) {
    return (
      <View style={[mqStyles.page, { alignItems: 'center', justifyContent: 'center' }]}>
        <ActivityIndicator
          size="large"
          color={tokens.colors.$houseRedDarken}
          accessibilityLabel="Refreshing rewards"
        />
      </View>
    );
  }

  if (!isFetching && !isAuthenticated && offerCardProps.length === 0) {
    return <UnauthenticatedWithNoOffers />;
  }

  return (
    <View style={mqStyles.page}>
      {shouldShowSectionsHeader && <SectionsHeader sections={sections} />}
      <ScrollView
        scrollEventThrottle={scrollEventThrottle}
        onScroll={onScroll}
        style={mqStyles.scroll}
        refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refreshRewards} />}
        keyboardShouldPersistTaps="handled"
        ref={scrollViewRef}
        onContentSizeChange={measureSectionPositions}
      >
        <View style={mqStyles.content}>
          {!shouldHideChallenges && (
            <>
              <View style={mqStyles.contentPadding} ref={challengesSectionRef}>
                <ChallengesSectionHeader />
              </View>
              <Challenges isLoading={isLoading} />
            </>
          )}
          <View style={mqStyles.contentPadding} ref={offersSectionRef}>
            <OffersSectionHeader
              hideCarouselButtons={hideCarouselButtons}
              isNextDisabled={isNextDisabled}
              isPrevDisabled={isPrevDisabled}
              nextCarouselPage={nextCarouselPage}
              prevCarouselPage={prevCarouselPage}
              onPressSeeAll={seeAllOffers}
            />
            <MqSwitch $ltDesktop={() => <PromoCode />} />
          </View>
          <MqSwitch
            $base={() => (
              <FlatList
                centerContent
                style={mqStyles.flatList}
                contentContainerStyle={mqStyles.offersContentFlatList}
                horizontal
                data={offerCardProps}
                renderItem={renderOffer}
                ListEmptyComponent={renderOffersEmpty}
                showsHorizontalScrollIndicator={false}
                ListFooterComponent={renderOffersFooter}
                ListHeaderComponent={listHeader}
              />
            )}
            $gteDesktop={() => (
              <>
                {offerCardProps.length === 0 ? (
                  renderOffersEmpty()
                ) : (
                  <View style={mqStyles.offersCarousel}>
                    <Carousel
                      renderCarouselLoading={renderCarouselLoading}
                      ref={carouselRef}
                      data={offerCardProps}
                      renderItem={renderOffer}
                      itemWidth={offerCardWidth}
                      onChangePage={setCarouselPaging}
                    />
                    {carouselPaging && carouselPaging.totalPages > 1 && (
                      <PaginationDots
                        style={mqStyles.carouselPagination}
                        selectedPage={carouselPaging.currentPage}
                        totalPages={carouselPaging.totalPages}
                        onPressPage={goToPage}
                      />
                    )}
                  </View>
                )}
              </>
            )}
          />
          <View style={mqStyles.contentPadding} ref={rewardsSectionRef}>
            <OffersDisclaimer />
            <RewardsSectionHeader onPressSeeHistory={seeRewardsHistory} />
          </View>
          {isAuthenticated ? (
            isRewardsTiersEnabled ? (
              <RewardTiers />
            ) : (
              <FlatList
                centerContent
                style={mqStyles.flatList}
                contentContainerStyle={mqStyles.rewardsContentFlatList}
                horizontal
                data={rewardCardProps}
                renderItem={renderReward}
                ListEmptyComponent={renderRewardsEmpty}
                showsHorizontalScrollIndicator={false}
                ListFooterComponent={renderRewardsFooter}
                ListHeaderComponent={listHeader}
              />
            )
          ) : (
            <View style={mqStyles.unauthenticatedContainer}>
              <GenericCard
                icon={<IconProfile fill={tokens.colors.$blackOpacity10} />}
                title={formatMessage({ id: 'signUpToGetFreeRewards' })}
                description={formatMessage({ id: 'earnPointsEnjoyExclusiveOffersAndRewards' })}
                buttonTitle={formatMessage({ id: 'signUpNow' })}
                buttonHandle={onPressSignUp}
              />
            </View>
          )}
          <View style={{ marginBottom: 160 + bottom }} />
        </View>
      </ScrollView>
    </View>
  );
}

const useMqStyles = createMqStyles({
  page: {
    $base: {
      flex: 1,
      backgroundColor: tokens.colors.$white,
    },
  },
  scroll: {
    $base: {
      flex: 1,
      width: '100%',
      backgroundColor: tokens.colors.$white,
    },
  },
  content: {
    $base: {
      flex: 1,
    },
    $gteDesktop: {
      width: 908,
      alignItems: 'center',
      alignSelf: 'center',
    },
  },
  contentPadding: {
    $base: {
      paddingHorizontal: 16,
      width: '100%',
    },
    $gteDesktop: {
      paddingHorizontal: 0,
    },
  },
  errorContainer: {
    $base: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 12,
    },
  },
  flatList: {
    $base: {
      width: '100%',
      marginTop: 16,
      flexGrow: 0,
    },
  },
  offersCarousel: {
    $base: {
      marginTop: 16,
      width: '100%',
    },
  },
  carouselPagination: {
    $base: {
      alignSelf: 'center',
      marginTop: 16,
    },
  },
  offersContentFlatList: {
    $base: {
      gap: 16,
    },
    $ltDesktop: {
      gap: 12,
    },
  },
  rewardsContentFlatList: {
    $base: {
      gap: 12,
      alignItems: 'flex-end',
      flexGrow: 0,
    },
  },
  seeMoreOffers: {
    $base: {
      height: 330,
      width: 215,
    },
    $ltDesktop: {
      width: 160,
      height: 280,
      marginRight: 16,
    },
  },
  seeMoreRewards: {
    $base: {
      height: 175,
      width: 215,
    },
    $ltDesktop: {
      height: 174,
      width: 160,
      marginRight: 16,
    },
  },
  unauthenticatedContainer: {
    $base: {
      borderRadius: 8,
      borderWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      marginTop: 12,
      paddingTop: 16,
      paddingHorizontal: 16,
      marginHorizontal: 16,
    },
    $gteDesktop: {
      width: '100%',
    },
  },
  listHeader: {
    $ltDesktop: {
      width: 4,
    },
  },
  emptyList: {
    $base: {
      flexGrow: 0,
    },
  },
  emptyListContent: {
    $base: {
      gap: 12,
    },
    $gteDesktop: {
      width: 908,
      marginTop: 16,
    },
  },
  emptyOffersContainer: {
    $gteDesktop: {
      marginTop: 16,
    },
  },
  noOffersAvailable: {
    $base: {
      maxWidth: 300,
      paddingVertical: 8,
    },
    $gteDesktop: {
      maxWidth: 600,
    },
  },
});
